/**
 * TanStack Query hooks for sender rewrites.
 *
 * Wraps:
 *   GET    /admin/sender-rewrites          -> useAdminSenderRewrites
 *   POST   /admin/sender-rewrites          -> useAddSenderRewrite
 *   PATCH  /admin/sender-rewrites/{id}     -> useUpdateSenderRewrite
 *   DELETE /admin/sender-rewrites/{id}     -> useDeleteSenderRewrite
 *
 * Each mutation invalidates the list query so the table reflects the
 * server state without a hard refresh.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import api from "@/lib/api";
import type {
  SenderRewriteCreate,
  SenderRewriteResponse,
  SenderRewriteUpdate,
} from "@/types/api";

export const ADMIN_SENDER_REWRITES_QUERY_KEY = [
  "admin",
  "sender-rewrites",
] as const;

export function useAdminSenderRewrites(): UseQueryResult<
  SenderRewriteResponse[]
> {
  return useQuery({
    queryKey: ADMIN_SENDER_REWRITES_QUERY_KEY,
    queryFn: async () => {
      const response = await api.get<SenderRewriteResponse[]>(
        "/admin/sender-rewrites",
      );
      return response.data;
    },
  });
}

export function useAddSenderRewrite(): UseMutationResult<
  SenderRewriteResponse,
  Error,
  SenderRewriteCreate
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body) => {
      const response = await api.post<SenderRewriteResponse>(
        "/admin/sender-rewrites",
        body,
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ADMIN_SENDER_REWRITES_QUERY_KEY,
      });
    },
  });
}

export function useUpdateSenderRewrite(): UseMutationResult<
  SenderRewriteResponse,
  Error,
  { id: string; body: SenderRewriteUpdate }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }) => {
      const response = await api.patch<SenderRewriteResponse>(
        `/admin/sender-rewrites/${id}`,
        body,
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ADMIN_SENDER_REWRITES_QUERY_KEY,
      });
    },
  });
}

export function useDeleteSenderRewrite(): UseMutationResult<
  void,
  Error,
  string
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      await api.delete(`/admin/sender-rewrites/${id}`);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ADMIN_SENDER_REWRITES_QUERY_KEY,
      });
    },
  });
}
